import { useEffect, useRef } from "react"
import * as Yup from "yup"

// Cloudflare Turnstile widget.
// Requires the script in public/index.html (loaded with ?render=explicit) and
// REACT_APP_TURNSTILE_SITE_KEY in .env. If the site key is missing the widget
// renders nothing, and the server skips verification when its key is missing too.
//
// Props:
//   onVerify(token)  called with a token when the check passes, "" when it expires/errors
//   action           stable name for this form (e.g. "contact"); the server checks it matches
//   resetKey         change this value (e.g. increment a counter) to reset the widget
//   error            optional error text to display under the widget

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

// True when a site key is configured. Pages without Formik use this to gate submits.
export const CAPTCHA_ENABLED = !!SITE_KEY

// Yup rule for a Formik `captchaToken` field. Required only when the widget is on.
export const captchaSchema = CAPTCHA_ENABLED
  ? Yup.string().required("Please complete the verification")
  : Yup.string()

const Turnstile = ({ onVerify, action, resetKey = 0, error }) => {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onVerifyRef = useRef(onVerify)
  onVerifyRef.current = onVerify

  useEffect(() => {
    if (!SITE_KEY) return

    let cancelled = false
    let timer = null

    const render = () => {
      if (cancelled || !containerRef.current) return
      if (!window.turnstile) {
        timer = setTimeout(render, 100) // script still loading
        return
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        action: action,
        callback: (token) => onVerifyRef.current(token),
        "expired-callback": () => onVerifyRef.current(""),
        "error-callback": () => onVerifyRef.current(""),
      })
    }
    render()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [action])

  useEffect(() => {
    if (resetKey && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
      onVerifyRef.current("")
    }
  }, [resetKey])

  if (!SITE_KEY) return null

  return (
    <div>
      <div ref={containerRef} />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}

export default Turnstile
